import { countLabel } from 'utils/ru';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
  useAppMetricaCatalog,
  useConnection,
  useConnectorTypes,
  useCreateAppMetricaSource,
  useProbeAppMetrica,
  useUpdateConnection,
} from 'api/connectors';
import { takeOAuthDraft } from 'api/oauth';
import { useWarehouses } from 'api/warehouse';
import { extractApiError, localizeApiMessage } from 'api/errors';
import OAuthConnectField from 'components/common/OAuthConnectField';
import { ColumnCard, ColumnTable } from './ColumnPicker';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';
import { translateMetadata } from 'i18n/metadata';

/*
 * AppMetrica source wizard — the mobile sibling of the BigQuery one.
 *
 *   1. Access   — a connected Yandex account (or a pasted token) and the app,
 *                 checked against the Management API.
 *   2. Sources  — which export tables to load, and which time the loading
 *                 window filters on (device time or server receipt time).
 *   3. Fields   — which columns each table carries. The catalog is static, so
 *                 this step reads no data and asks AppMetrica nothing.
 *   4. Schedule — destination, cron, start date and an optional backfill.
 *
 * Nothing is saved until step 4; the probes run stateless on this page's
 * state, and the token never reaches the browser when an account is used —
 * the probe resolves it server-side from the grant. The same page edits an
 * existing source (`/connectors/appmetrica/:id/edit`): the token is never
 * re-typed (blank means keep), the schedule half belongs to the pipeline and
 * is not shown, and the save is a PATCH of the connection alone.
 */

const STEPS = () => ([t('data.access'), t('data.sources'), t('data.fields'), t('data.schedule')]);
const CONNECTOR_SLUG = 'yandex-appmetrica';
const PROVIDER = 'yandex';

const CRON_PRESETS = [
  { get label() { return t('data.dailyAt3Am'); }, value: '0 3 * * *' },
  { get label() { return t('data.dailyAt7Am'); }, value: '0 7 * * *' },
  { get label() { return t('data.every6Hours'); }, value: '0 */6 * * *' },
  { get label() { return t('data.hourly'); }, value: '0 * * * *' },
];
const DEFAULT_CRON = '0 3 * * *';
const BACKFILL_SLICE_DAYS = 7;

const PROBE_ERROR_HINTS = {
  get auth() { return t('data.appmetricaRefusedTheTokenItNeedsTheAppmetricaReadPermissionAnd'); },
  get not_found() { return t('data.noSuchApplicationCheckTheIdOrPickTheAppFrom'); },
  get unreachable() { return t('data.appmetricaDidNotAnswerThisIsRetryableTryAgainInA'); },
  get rate_limit() { return t('data.tooManyRequestsInARowNothingIsWrongWithThe'); },
  get config() { return t('data.theTokenOrTheApplicationIdIsMalformedSeeTheMessage'); },
  get grant_inactive() { return t('data.theConnectedAccountNoLongerWorksReconnectItOrConnectAnother'); },
};

function daysBetween(from, to) {
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
  return Math.round((end - start) / 86_400_000);
}

// Catalog fields in the shape the shared column list draws.
function asColumns(table) {
  return table.fields.map((field) => ({
    path: field.name,
    type: field.type,
    description: translateMetadata(field.description),
    carried_as: 'scalar',
  }));
}

// The columns a table always carries: its key and the time column of the
// chosen dimension. The server adds them anyway; ticking them here says so.
function requiredFields(table, dimension) {
  return [...table.key_properties, table.time_columns[dimension]].filter(
    (name, index, names) => names.indexOf(name) === index,
  );
}

export default function AppMetricaSourceWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { connectionId } = useParams();
  const editing = Boolean(connectionId);
  const probe = useProbeAppMetrica();
  const createSource = useCreateAppMetricaSource();
  const updateConnection = useUpdateConnection(connectionId);
  const { data: connection } = useConnection(connectionId);
  const { data: types } = useConnectorTypes();
  const { data: catalog, isLoading: catalogLoading } = useAppMetricaCatalog();
  const { data: warehouses } = useWarehouses();

  const connectorType = useMemo(
    () => (types || []).find((t) => t.slug === CONNECTOR_SLUG) || null,
    [types],
  );
  const auth = connectorType?.auth?.kind === 'oauth' ? connectorType.auth : null;
  const oauthErrorCode = searchParams.get('oauth_error');

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);

  // Step 1 — access.
  const [grantId, setGrantId] = useState(null);
  const [authToken, setAuthToken] = useState('');
  const [applicationId, setApplicationId] = useState('');
  // The app as AppMetrica describes it, once the check passed for THIS id.
  const [checkedApp, setCheckedApp] = useState(null);
  // Apps a typed token can see, when the human asked for the list.
  const [apps, setApps] = useState(null);

  // Step 2 — sources.
  const [dimension, setDimension] = useState('default');
  // table name → array of ticked field names.
  const [sources, setSources] = useState({});
  // Step 3 — the column filter per table, and the column under the mouse
  // (with the table it belongs to, so only that table's card describes it).
  const [filters, setFilters] = useState({});
  const [inspect, setInspect] = useState(null);

  // Step 4 — destination and cadence.
  const [connectionName, setConnectionName] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [cron, setCron] = useState(DEFAULT_CRON);
  const [startDate, setStartDate] = useState(() =>
    new Date(Date.now() - 86_400_000).toISOString().slice(0, 10),
  );
  const [backfill, setBackfill] = useState({ enabled: false, start: '', end: '' });

  const tablesByName = useMemo(
    () => Object.fromEntries((catalog?.tables || []).map((t) => [t.name, t])),
    [catalog],
  );

  // Back from the consent screen: the callback appends ?oauth_grant= (or
  // ?oauth_error=) and the page's state was parked in sessionStorage.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    const draft = takeOAuthDraft(PROVIDER);
    const grantParam = searchParams.get('oauth_grant');
    if (!draft && !grantParam) return;
    if (draft?.wizard === CONNECTOR_SLUG) {
      setAuthToken(draft.authToken || '');
      setApplicationId(draft.applicationId || '');
      setDimension(draft.dimension || 'default');
      setSources(draft.sources || {});
      setConnectionName(draft.connectionName || '');
    }
    if (grantParam) setGrantId(grantParam);
  }, [searchParams]);

  // Fill the wizard from the saved connection, once.
  const prefilled = useRef(false);
  useEffect(() => {
    const saved = connection?.appmetrica_sources;
    if (!editing || prefilled.current || !saved || !catalog) return;
    prefilled.current = true;
    setApplicationId(saved.application_id || '');
    setDimension(saved.date_dimension || 'default');
    setStartDate(saved.start_date || '');
    setConnectionName(connection.name || '');
    if (connection.oauth_grant?.public_id && !grantId) setGrantId(connection.oauth_grant.public_id);
    setSources(
      Object.fromEntries(
        (saved.sources || [])
          .filter((entry) => tablesByName[entry.table])
          .map((entry) => [
            entry.table,
            // An empty saved list means the table's curated defaults.
            entry.fields?.length ? entry.fields : [...tablesByName[entry.table].default_fields],
          ]),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, editing, catalog]);

  const activeWarehouses = useMemo(
    () => (warehouses || []).filter((w) => w.is_active),
    [warehouses],
  );
  const chosen = useMemo(
    () => Object.keys(tablesByName).filter((name) => sources[name]),
    [sources, tablesByName],
  );

  // How a probe reaches AppMetrica: the saved connection in edit mode (unless
  // a new token was pasted), else the grant, else the typed token.
  const probeRoute = () => {
    if (editing && !authToken.trim() && !grantId) return { connectionId };
    if (grantId) return { grantId };
    return { config: { auth_token: authToken.trim() } };
  };

  const runProbe = async (asked) => {
    setError(null);
    const result = await probe.mutateAsync({ ...probeRoute(), ...asked });
    if (!result.reachable) {
      setError(
        PROBE_ERROR_HINTS[result.error] || localizeApiMessage(result.hint, null, t('data.theCheckFailed')),
      );
      return null;
    }
    return result;
  };

  // ── step 1 ─────────────────────────────────────────────────────────────────

  const listApps = async () => {
    const result = await runProbe({ applicationId: '' });
    if (result) setApps(result.applications);
  };

  const checkAccess = async () => {
    const result = await runProbe({ applicationId: applicationId.trim() });
    if (!result) {
      setCheckedApp(null);
      return;
    }
    setCheckedApp(result.application);
    setConnectionName((name) => name || t('data.appmetricaValue', { value1: result.application.name || applicationId }));
  };

  // A different app or token voids the green verdict.
  const changeApplication = (value) => {
    setApplicationId(value);
    setCheckedApp(null);
  };

  // ── step 2 ─────────────────────────────────────────────────────────────────

  const toggleTable = (table) => {
    setSources((current) => {
      if (current[table.name]) {
        const { [table.name]: _dropped, ...rest } = current;
        return rest;
      }
      return { ...current, [table.name]: [...table.default_fields] };
    });
  };

  // ── step 3 ─────────────────────────────────────────────────────────────────

  const toggleField = (tableName, name) => {
    const table = tablesByName[tableName];
    if (requiredFields(table, dimension).includes(name)) return;
    setSources((current) => {
      const picked = current[tableName] || [];
      const next = picked.includes(name) ? picked.filter((f) => f !== name) : [...picked, name];
      return { ...current, [tableName]: next };
    });
  };

  const setFields = (tableName, names) =>
    setSources((current) => ({ ...current, [tableName]: names }));

  // ── step 4 ─────────────────────────────────────────────────────────────────

  const estimate = useMemo(
    () => daysBetween(backfill.start, backfill.end),
    [backfill.start, backfill.end],
  );

  const saving = createSource.isPending || updateConnection.isPending;

  const connectionConfig = () => ({
    // Blank on edit means "keep the stored token"; with an account the server
    // drops a typed token anyway.
    auth_token: grantId ? '' : authToken.trim(),
    application_id: applicationId.trim(),
    start_date: startDate,
    date_dimension: dimension,
    sources: chosen.map((name) => {
      const table = tablesByName[name];
      const required = requiredFields(table, dimension);
      return {
        table: name,
        fields: [...sources[name], ...required.filter((f) => !sources[name].includes(f))],
      };
    }),
  });

  const saveEdit = async () => {
    setError(null);
    try {
      await updateConnection.mutateAsync({
        name: connectionName.trim(),
        config: connectionConfig(),
        ...(grantId ? { oauth_grant_id: grantId } : {}),
      });
      navigate(`/console/connectors/${connectionId}`);
    } catch (exc) {
      setError(extractApiError(exc));
    }
  };

  const save = async () => {
    if (editing) return saveEdit();
    setError(null);
    const payload = {
      connection: {
        name: connectionName.trim(),
        connector_type_slug: CONNECTOR_SLUG,
        config: connectionConfig(),
        ...(grantId ? { oauth_grant_id: grantId } : {}),
      },
      pipeline: {
        name: t('data.warehouseFlow', { name: connectionName.trim() }),
        warehouse_id: warehouseId,
        schedule_cron: cron,
      },
      ...(backfill.enabled && estimate
        ? {
            backfill: {
              start_date: backfill.start,
              end_date: backfill.end,
              slice_days: BACKFILL_SLICE_DAYS,
            },
          }
        : {}),
    };
    try {
      await createSource.mutateAsync(payload);
      navigate('/console/pipelines');
    } catch (exc) {
      setError(extractApiError(exc));
    }
  };

  const hasCredential = Boolean(grantId || authToken.trim() || editing);
  const canLeaveStep = [
    editing ? Boolean(applicationId.trim()) : Boolean(checkedApp),
    chosen.length > 0,
    chosen.every((name) => (sources[name] || []).length > 0),
    Boolean(connectionName.trim() && (editing || warehouseId) && startDate),
  ];

  const draft = {
    wizard: CONNECTOR_SLUG,
    authToken,
    applicationId,
    dimension,
    sources,
    connectionName,
  };
  const returnTo = editing
    ? `/console/connectors/appmetrica/${connectionId}/edit`
    : '/console/connectors/new/appmetrica';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton
          onClick={() =>
            navigate(editing ? `/console/connectors/${connectionId}` : '/console/connectors')
          }
          size="small"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          {editing ? connectionName || t('data.appmetricaSource') : t('data.appmetricaSource')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{t('data.browsingCostsNothingTheCheckReadsTheAppSNameFrom')}{' '}
        {editing
          ? t('data.nothingChangesUntilYouSaveOnTheLastStep')
          : t('data.nothingIsSavedUntilTheLastStep')}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4, maxWidth: 720 }}>
        {STEPS().map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, maxWidth: 720 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {activeStep === 0 && (
        <Box sx={{ maxWidth: 720 }}>
          {auth && (
            <>
              <OAuthConnectField
                auth={auth}
                connectorTypeSlug={CONNECTOR_SLUG}
                grantId={grantId}
                onGrantChange={(value) => {
                  setGrantId(value);
                  setCheckedApp(null);
                  setApps(null);
                }}
                resourceValue={applicationId}
                onResourceChange={changeApplication}
                returnTo={returnTo}
                draft={draft}
                errorCode={oauthErrorCode}
              />
              <Divider sx={{ my: 2 }} />
            </>
          )}
          {!grantId && (
            <TextField
              label={editing ? t('data.oauthTokenLeaveBlankToKeep') : t('data.oauthToken')}
              type="password"
              helperText={
                editing
                  ? t('data.theStoredTokenNeverLeavesTheServerPasteANewOne')
                  : t('data.onlyWithoutAConnectedAccountATokenWithAppmetricaReadObtained')
              }
              value={authToken}
              onChange={(e) => {
                setAuthToken(e.target.value);
                setCheckedApp(null);
                setApps(null);
              }}
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
          {!grantId && hasCredential && (
            <Box sx={{ mb: 2 }}>
              <Button
                size="small"
                variant="text"
                startIcon={probe.isPending ? <CircularProgress size={14} /> : <SearchIcon />}
                disabled={probe.isPending}
                onClick={listApps}
              >{t('data.listTheAppsThisTokenCanSee')}</Button>
              {apps && (
                <TextField
                  select
                  size="small"
                  label={t('data.application')}
                  value={apps.some((a) => a.id === applicationId) ? applicationId : ''}
                  onChange={(e) => changeApplication(e.target.value)}
                  fullWidth
                  sx={{ mt: 1 }}
                  helperText={apps.length ? '' : t('data.theTokenSeesNoApps')}
                >
                  {apps.map((app) => (
                    <MenuItem key={app.id} value={app.id}>
                      {app.name ? `${app.name} — ${app.id}` : app.id}
                      {app.bundle_id ? ` (${app.bundle_id})` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          )}
          <TextField
            label={t('data.applicationId')}
            helperText={t('data.theNumericIdOfTheAppInAppmetrica')}
            value={applicationId}
            onChange={(e) => changeApplication(e.target.value.replace(/[^0-9]/g, ''))}
            required
            fullWidth
            sx={{ mb: 3 }}
          />
          <Button
            variant="outlined"
            startIcon={probe.isPending ? <CircularProgress size={16} /> : <TravelExploreIcon />}
            disabled={!applicationId.trim() || !hasCredential || probe.isPending}
            onClick={checkAccess}
          >{t('data.checkAccess')}</Button>
          {checkedApp && (
            <Alert icon={<CheckCircleIcon />} severity="success" sx={{ mt: 2 }}>{t('data.theTokenCanRead')}{' '}<strong>{checkedApp.name || checkedApp.id}</strong>
              {checkedApp.bundle_id ? ` (${checkedApp.bundle_id})` : ''}
              {checkedApp.permission ? t('data.accessLevelValue', { value1: checkedApp.permission }) : ''}.
            </Alert>
          )}
        </Box>
      )}

      {activeStep === 1 && (
        <Box sx={{ maxWidth: 860 }}>
          {catalogLoading && <CircularProgress size={20} />}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.eachTableBecomesOneTableInTheWarehouseLoadedADay')}</Typography>
          <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            {(catalog?.tables || []).map((table) => (
              <Box
                key={table.name}
                sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Checkbox
                    checked={Boolean(sources[table.name])}
                    onChange={() => toggleTable(table)}
                    sx={{ mt: -1 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontFamily: 'monospace' }}>{table.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translateMetadata(table.title)}
                      </Typography>
                      {table.key_properties.length > 0 && (
                        <Tooltip title={t('data.rowsAreIdentifiedByTheseColumnsWhereTheWarehouseMerges')}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('data.sourceKey', { name: table.key_properties.join(', ') })}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {translateMetadata(table.description)}{' '}{t('data.windowedBy')}{' '}
                      <code>{table.time_columns[dimension]}</code>.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Paper>

          <Typography variant="subtitle2" gutterBottom>{t('data.whichTimeTheLoadingWindowFiltersOn')}</Typography>
          <RadioGroup value={dimension} onChange={(e) => setDimension(e.target.value)}>
            <FormControlLabel
              value="default"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">{t('data.timeOnTheDeviceTheMomentTheEventHappened')}</Typography>
              }
            />
            <FormControlLabel
              value="receive"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">{t('data.timeTheServerReceivedItCatchesEventsADeviceSentLate')}</Typography>
              }
            />
          </RadioGroup>
        </Box>
      )}

      {activeStep === 2 && (
        <Box sx={{ maxWidth: 1160 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.theKeyColumnsAndTheTimeColumnAreAlwaysCarriedThe')}</Typography>
          {chosen.map((name) => {
            const table = tablesByName[name];
            const columns = asColumns(table);
            const picked = sources[name] || [];
            const required = requiredFields(table, dimension);
            const pickedSet = new Set([...picked, ...required]);
            const needle = (filters[name] || '').trim().toLowerCase();
            const shown = needle
              ? columns.filter(
                  (column) =>
                    column.path.toLowerCase().includes(needle) ||
                    (column.description || '').toLowerCase().includes(needle),
                )
              : columns;
            return (
              <Paper key={name} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography sx={{ fontFamily: 'monospace' }}>{name}</Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t('data.valueOfValueFields', { value1: pickedSet.size, value2: columns.length })}
                  />
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mt: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Button size="small" onClick={() => setFields(name, columns.map((c) => c.path))}>{t('data.selectAll')}</Button>
                      <Button size="small" onClick={() => setFields(name, [...table.default_fields])}>{t('data.curatedSet')}</Button>
                      <Button size="small" onClick={() => setFields(name, [])}>{t('data.clear')}</Button>
                      <TextField
                        size="small"
                        placeholder={t('data.filterByNameOrDescription')}
                        value={filters[name] || ''}
                        onChange={(e) => setFilters({ ...filters, [name]: e.target.value })}
                        sx={{ width: 280 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Box sx={{ flex: 1 }} />
                      {needle && (
                        <Typography variant="caption" color="text.secondary">
                          {shown.length}{' '}{t('data.of')}{' '}{columns.length}{' '}{t('data.shown')}</Typography>
                      )}
                    </Box>
                    <ColumnTable
                      columns={shown}
                      picked={pickedSet}
                      onToggle={(path) => toggleField(name, path)}
                      onInspect={(column) => setInspect(column ? { table: name, column } : null)}
                      emptyNote="No field matches that filter."
                    />
                    {!picked.length && (
                      <Alert severity="warning" sx={{ mt: 1.5 }}>{t('data.nothingIsTickedBeyondTheRequiredColumnsTickTheFieldsYou')}</Alert>
                    )}
                  </Box>
                  <Box sx={{ width: 264, flexShrink: 0 }}>
                    <Typography variant="overline" color="text.secondary">{t('data.column')}</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <ColumnCard column={inspect?.table === name ? inspect.column : null} />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary">{t('data.alwaysCarried')}{' '}{required.join(', ')}.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {activeStep === 3 && (
        <Box sx={{ maxWidth: 720 }}>
          <TextField
            label={t('data.connectionName')}
            value={connectionName}
            onChange={(e) => setConnectionName(e.target.value)}
            required
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            type="date"
            label={t('data.startLoadingFrom')}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            error={!startDate}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 3 }}
            helperText={t('data.theFirstDayAScheduledRunMayReadYesterdayByDefault')}
          />
          {editing ? (
            <Alert severity="info" sx={{ mb: 3 }}>{t('data.destinationScheduleAndHistoryBelongToThePipelineNotToThis')}</Alert>
          ) : (
            <>
              <TextField
                select
                label={t('data.destinationWarehouse')}
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                required
                fullWidth
                sx={{ mb: 2 }}
              >
                {activeWarehouses.map((w) => (
                  <MenuItem key={w.public_id} value={w.public_id}>
                    {w.name}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({w.engine_display})
                    </Typography>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('data.schedule')}
                value={cron}
                onChange={(e) => setCron(e.target.value)}
                fullWidth
                sx={{ mb: 3 }}
              >
                {CRON_PRESETS.map((preset) => (
                  <MenuItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </MenuItem>
                ))}
              </TextField>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={backfill.enabled}
                      onChange={(e) => setBackfill({ ...backfill, enabled: e.target.checked })}
                    />
                  }
                  label={t('data.loadHistoryNow')}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>{t('data.offByDefaultHistoryIsExportedADayAtATime')}{' '}{BACKFILL_SLICE_DAYS}{' '}{t('data.daysPerRunAppmetricaPreparesEachExportInItsOwnQueue')}</Typography>
                {backfill.enabled && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      type="date"
                      label={t('data.from')}
                      value={backfill.start}
                      onChange={(e) => setBackfill({ ...backfill, start: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      type="date"
                      label={t('data.to')}
                      value={backfill.end}
                      onChange={(e) => setBackfill({ ...backfill, end: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      helperText={t('data.exclusiveTheFirstDayNotLoaded')}
                    />
                  </Box>
                )}
                {backfill.enabled && estimate > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {countLabel(estimate, 'day')} × {countLabel(chosen.length, 'table')} = {countLabel(estimate * chosen.length, 'export')}.
                  </Alert>
                )}
              </Paper>
            </>
          )}

          <Button variant="contained" disabled={!canLeaveStep[3] || saving} onClick={save}>
            {saving ? t('data.saving') : editing ? t('data.saveChanges') : t('data.createSource')}
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 4 }}>
        <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>{t('data.back')}</Button>
        {activeStep < STEPS().length - 1 && (
          <Button
            variant="contained"
            disabled={!canLeaveStep[activeStep] || probe.isPending}
            onClick={() => setActiveStep((s) => s + 1)}
          >{t('data.next')}</Button>
        )}
      </Box>
    </Box>
  );
}
